/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    INSERT_TABLE_COMMAND,
    TableCellNode,
    TableNode,
    TableRowNode,
} from '@lexical/table';
import { EditorThemeClasses, Klass, LexicalEditor, LexicalNode } from 'lexical';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { useDialog } from '@cms/providers/DialogProvider';
import { SystemTextInput } from '../../../data/TextInput';

export type InsertTableCommandPayload = Readonly<{
    columns: string;
    rows: string;
    includeHeaders?: boolean;
}>;

export type CellContextShape = {
    cellEditorConfig: null | CellEditorConfig;
    cellEditorPlugins: null | JSX.Element | Array<JSX.Element>;
    set: (
        cellEditorConfig: null | CellEditorConfig,
        cellEditorPlugins: null | JSX.Element | Array<JSX.Element>,
    ) => void;
};

export type CellEditorConfig = Readonly<{
    namespace: string;
    nodes?: ReadonlyArray<Klass<LexicalNode>>;
    onError: (error: Error, editor: LexicalEditor) => void;
    readOnly?: boolean;
    theme?: EditorThemeClasses;
}>;

export const CellContext = createContext<CellContextShape>({
    cellEditorConfig: null,
    cellEditorPlugins: null,
    set: () => {
        // Empty
    },
});

export function TableContext({ children }: { children: JSX.Element }) {
    const [contextValue, setContextValue] = useState<{
        cellEditorConfig: null | CellEditorConfig;
        cellEditorPlugins: null | JSX.Element | Array<JSX.Element>;
    }>({
        cellEditorConfig: null,
        cellEditorPlugins: null,
    });
    return (
        <CellContext.Provider
            value={useMemo(
                () => ({
                    cellEditorConfig: contextValue.cellEditorConfig,
                    cellEditorPlugins: contextValue.cellEditorPlugins,
                    set: (cellEditorConfig, cellEditorPlugins) => {
                        setContextValue({ cellEditorConfig, cellEditorPlugins });
                    },
                }),
                [contextValue.cellEditorConfig, contextValue.cellEditorPlugins],
            )}>
            {children}
        </CellContext.Provider>
    );
}

export function InsertTableDialog({
    activeEditor,
}: {
    activeEditor: LexicalEditor;
}): JSX.Element {
    const { closeDialog } = useDialog()

    const [rows, setRows] = useState('5');
    const [columns, setColumns] = useState('5');

    const onClick = () => {
        activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
            columns,
            rows,
        });

        closeDialog()
    };

    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Insert Table',
                maxWidth: 'sm'
            }}
            // trigger={{
            //     component: (
            //         <CiViewTable size={24} />
            //     ),
            // }}
            content={{
                component: (
                    <div
                        className='space-y-4'
                    >
                        <SystemTextInput
                            placeholder={'# of rows (1-500)'}
                            label="Rows"
                            onChange={(e) => setRows(e.target.value)}
                            value={rows}
                            type="number"
                        />
                        <SystemTextInput
                            placeholder={'# of columns (1-50)'}
                            label="Columns"
                            onChange={(e) => setColumns(e.target.value)}
                            value={columns}
                            type="number"
                        />
                    </div>
                ),
            }}
            actionsProps={{
                submit: {
                    onClick: onClick
                },
                cancel: {
                    onClick: closeDialog
                }
            }}
        />
    );
}

export function TablePlugin({
    cellEditorConfig,
    children,
}: {
    cellEditorConfig: CellEditorConfig;
    children: JSX.Element | Array<JSX.Element>;
}): JSX.Element | null {
    const [editor] = useLexicalComposerContext();
    const cellContext = useContext(CellContext);
    useEffect(() => {
        if (!editor.hasNodes([TableNode, TableRowNode, TableCellNode])) {
            throw new Error(
                'TablePlugin: TableNode, TableRowNode, or TableCellNode is not registered on editor',
            );
        }
    }, [editor]);
    useEffect(() => {
        cellContext.set(cellEditorConfig, children);
    }, [cellContext, cellEditorConfig, children]);
    return null;
}
